import fp from 'lodash/fp';
import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { branch, lifecycle, renderComponent, renderNothing, withHandlers, withProps } from 'recompose';
import { bindActionCreators, compose } from 'redux';
import { defaultIndustrySettings, routes, tabKeys } from '../../../../constants';
import { fullName, isBusinessAdmin } from '../../../../services';
import { businessActions, businessSelector, currentTeamMembersSelector, teamMembersSelector } from '../../../../state/modules/business';
import { currentUserIndustry, currentUserSelector } from '../../../../state/modules/user';
import { possessiveCase } from '../../../../utils';
import Loading from '../../loading';
import AccountSettings from '../account-settings';
import { CpdSettings } from '../account-settings/components';

const UserTrainingPlan = compose(
  connect((state, { user }) => ({
    business: businessSelector(state),
    userIndustryType: fp.flow(currentUserIndustry, fp.first, fp.get('industryType'))(state),
    industryCpdCategorys: state.industry.cpdCategorys,
    industryRole: fp.head(state.business.user.industryRoles),
    industryRolesIsFetching: state.business.user.industryRolesIsFetching,
    isFetchingIndustry: state.industry.isFetching,
    isUpdating: state.business.user.isUpdating,
    isFetchingData: state.business.user.isFetching,
    isFetchingFullTeamCpdCategorys: state.business.isFetchingTeamCpdCategorys,
    isFetchingTeamCpdCategorys: state.business.team.isFetchingTeamCpdCategorys,
    fullTeamCpdCategorys: state.business.teamCpdCategorys,
    currentTeamCpdCategorys: state.business.team.cpdCategorys,
    error: state.user.error
  }), (dispatch) => ({
    getIndustryRoles: bindActionCreators(businessActions.business.user.getIndustryRoles, dispatch),
    getFullTeamCpdCategorys: bindActionCreators(businessActions.business.getFullTeamCpdCategorys, dispatch),
    getTeamCpdCategorys: bindActionCreators(businessActions.business.team.getTeamCpdCategorys, dispatch),
    saveUserCpdCategorys: bindActionCreators(businessActions.business.user.setCpdCategorys, dispatch)
  })),
  branch(
    ({ business, user }) => !business || !user,
    renderNothing
  ),
  lifecycle({
    componentDidMount() {
      const {
        business,
        getIndustryRoles,
        getTeamCpdCategorys,
        getFullTeamCpdCategorys,
        industryRolesIsFetching,
        isFetchingFullTeamCpdCategorys,
        isFetchingTeamCpdCategorys,
        user,
        teamId
      } = this.props;

      if (!industryRolesIsFetching) {
        getIndustryRoles(business.id, user.id);
      }

      if ((!teamId) && (!isFetchingFullTeamCpdCategorys)) {
        getFullTeamCpdCategorys(business.id);
      }

      if (teamId && !isFetchingTeamCpdCategorys) {
        getTeamCpdCategorys(business.id, teamId)
      }
    }
  }),
  withProps(({
    fullTeamCpdCategorys,
    currentTeamCpdCategorys,
    teamId
  }) => ({
    teamCpdCategorys: teamId ? currentTeamCpdCategorys : fullTeamCpdCategorys
  })),
  branch(
    ({ industryRole, teamCpdCategorys }) => !industryRole || !teamCpdCategorys,
    renderComponent(Loading)
  ),
  withProps(({
    industryRole,
    isFetchingIndustry,
    isFetchingData,
    teamCpdCategorys,
    user
  }) => ({
    cpdCategorys: fp.flow(
      fp.find(fp.flow(fp.get('userId'), fp.eq(user.id))),
      fp.get('cpdCategorys'),
      fp.map(
        (aoa) => [aoa].concat(aoa.children)
      ),
      fp.flatten,
      fp.map(
        ({ cpdCategoryId, primaryStat }) => ({
          cpdCategoryId: cpdCategoryId,
          totalCpdPoints: primaryStat.goal
        })
      )
    )(teamCpdCategorys),
    isLoading: isFetchingData || isFetchingIndustry,
    industry: {
      ...defaultIndustrySettings,
      ...industryRole
    },
    cpdCategoryTransform: (cpdCategory) => ({
      ...cpdCategory,
      userId: 0 // Dummy user id so the server accepts the JSON.
    }),
    headings: {
      cpdCategorys: [
        <p key='heading'>
          <strong>
            Your CPD categories
          </strong>
        </p>,
        <p key='copy'>
          Specify the knowledge areas you are permitted to provide general and personal advice for.
        </p>
      ],
      cpdDate: [
        <p key='heading'>
          <strong>
            Enter the last day of the CPD Year
          </strong>
        </p>,
        <p key='copy'>
          Your CPD anniversary date is the annual year in which your CPD track against.
          Our default CPD year runs from July 1 - June 30.
          You can change this at any time.
        </p>
      ],
      cpdPoints: [
        <p key='heading'>
          <strong>
            How many CPD hours do you need to earn in a year?
          </strong>
        </p>,
        <p key='copy'>
          Specify the total number of CDP hours you are required to complete each year.
        </p>
      ],
      cpdAllocation: [
        <p key='heading'>
          <strong>
            CPD allocation
          </strong>
        </p>,
        <p key='copy'>
          Specify the number of CPD hours you are required to meet for each CPD category you provide services on.
        </p>
      ]
    }
  })),
  withHandlers({
    saveCpdCategorys: ({
      saveUserCpdCategorys,
      business: {
        id: businessId
      },
      user: {
        id: userId
      }
    }) => ({
      industry,
      ...payload
    }) => saveUserCpdCategorys({
      ...payload,
      industry,
      businessId,
      userId
    })
  })
)(CpdSettings);

const TeamUserAccountSettings = compose(
  withRouter,
  connect((state) => ({
    user: currentUserSelector(state),
    allTeamMembers: teamMembersSelector(state),
    currentTeamMembers: currentTeamMembersSelector(state),
    business: businessSelector(state)
  }), (dispatch) => ({
    getTeam: bindActionCreators(businessActions.business.team.getTeam, dispatch)
  })),
  withProps(({
    match: {
      params: {
        team,
        user
      }
    }
  }) => ({
    contextBusinessTeamId: team,
    contextUserId: user
  })),
  lifecycle({
    componentDidMount() {
      const {
        business,
        contextBusinessTeamId,
        getTeam
      } = this.props;

      if (contextBusinessTeamId) {
        getTeam(business.id, contextBusinessTeamId);
      }
    }
  }),
  withProps(({
    user,
    allTeamMembers,
    currentTeamMembers
  }) => ({
    teamMembers: isBusinessAdmin(user) ? allTeamMembers : currentTeamMembers
  })),
  branch(({ teamMembers }) => !teamMembers, renderComponent(Loading)),
  withProps(({
    contextUserId,
    teamMembers
  }) => ({
    ...fp.flow(
      fp.map(fp.get('user')),
      fp.find(
        fp.flow(
          fp.get('id'),
          fp.eq(parseInt(contextUserId, 10)),
        )
      ),
      fp.over([fp.identity, fullName]),
      ([contextUser, userName]) => ({
        contextUser,
        userName
      })
    )(teamMembers)
  })),
  withProps(({
    contextUser,
    userName,
    contextBusinessTeamId
  }) => ({
    routes: (tabKey) => routes.app.teams.userSettings(userName, tabKey),
    title: `${possessiveCase(userName)} Settings`,
    tabs: [
      {
        path: tabKeys.businessUserSettings.trainingPlan,
        title: 'Training plan',
        content: <UserTrainingPlan user={contextUser} teamId={contextBusinessTeamId} planOwner={userName} />
      }
    ]
  }))
)(AccountSettings);

export default TeamUserAccountSettings;



// WEBPACK FOOTER //
// ./src/containers/pages/app/teams/user-account-settings.js